import i18n from "./index";
import { formatBytes } from "../helpers/bytes";
import { ENABLE_PRO_PLAN } from "../constants/constants";

export const errorTranslationKey = (code: string): string => {
  if (!ENABLE_PRO_PLAN && (
    code === "CROSS_TIER_MEDIA" ||
    code === "PLAN_FEATURE_UNAVAILABLE" ||
    code === "UNSUPPORTED_RESOLUTION"
  )) {
    return `errors:${code}_BASIC`;
  }
  return `errors:${code}`;
};

export interface ApiErrorDetails {
  action?: string;
  feature?: string;
  playlistTier?: string;
  mediaTier?: string;
  reason?: string;
  status?: string;
  upgradeTo?: string[];
  bytesAvailable?: number;
  bytesToDelete?: number;
  capBytes?: number;
  usedBytes?: number;
  reservedBytes?: number;
  newCapBytes?: number;
  [key: string]: unknown;
}

export const API_ERROR_CODES = {
  PLAN_FEATURE_UNAVAILABLE: "PLAN_FEATURE_UNAVAILABLE",
  PLAN_NOT_SUPPORTED: "PLAN_NOT_SUPPORTED",
  STREAM_START_BLOCKED: "STREAM_START_BLOCKED",
  STREAM_UPLOAD_BLOCKED: "STREAM_UPLOAD_BLOCKED",
  SUBSCRIPTION_STATE_INVALID: "SUBSCRIPTION_STATE_INVALID",
  TRIAL_ALREADY_USED: "TRIAL_ALREADY_USED",
} as const;

export type CurrentApiErrorCode =
  (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];

export class ApiError extends Error {
  public readonly code: string | undefined;
  public readonly fallbackMessage: string;
  public readonly status: number;
  public readonly details?: ApiErrorDetails;

  constructor(
    code: string | undefined,
    fallbackMessage: string,
    status: number,
    details?: ApiErrorDetails
  ) {
    super(fallbackMessage);
    this.name = "ApiError";
    this.code = code;
    this.fallbackMessage = fallbackMessage;
    this.status = status;
    this.details = details;
    // Ensure instanceof works when transpiled to ES5
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

// Plan enum values → user-facing labels.
const TIER_LABEL: Record<string, string> = {
  basic: "Basic",
  r1080p: "1080p",
  r2160p: "4K",
};

export const toLabel = (tier: string | undefined): string | undefined =>
  tier ? TIER_LABEL[tier] ?? tier : undefined;

const BYTE_KEYS = [
  "bytesAvailable",
  "bytesToDelete",
  "capBytes",
  "usedBytes",
  "reservedBytes",
  "newCapBytes",
] as const;

// STREAM_START_BLOCKED / STREAM_UPLOAD_BLOCKED carry `details.reason` as a
// raw StartBlockedReason/UploadBlockedReason token (e.g. "over_cap") — the
// namespace under liveStream.json holding the human copy for each.
const REASON_NAMESPACE: Record<string, string> = {
  STREAM_START_BLOCKED: "startBlocked",
  STREAM_UPLOAD_BLOCKED: "uploadBlocked",
};

/** Prepares `details` for i18n interpolation: bytes → "12.3 GB", plans → labels,
 * a start/upload-blocked `reason` token → its liveStream:*Blocked.<reason> copy. */
export function formatDetails(
  details: ApiErrorDetails | undefined,
  code?: string
): Record<string, unknown> | undefined {
  if (!details) return undefined;
  const out: Record<string, unknown> = { ...details };
  for (const key of BYTE_KEYS) {
    const v = details[key];
    if (typeof v === "number") out[key] = formatBytes(v);
  }
  if (Array.isArray(details.upgradeTo)) {
    out.upgradeTo = (details.upgradeTo as string[]).map(toLabel).join(" or ");
  }
  out.playlistTier = toLabel(details.playlistTier);
  out.mediaTier = toLabel(details.mediaTier);
  const reasonNamespace = code ? REASON_NAMESPACE[code] : undefined;
  if (reasonNamespace && typeof details.reason === "string") {
    const reasonKey = `liveStream:${reasonNamespace}.${details.reason}`;
    out.reason = i18n.exists(reasonKey) ? i18n.t(reasonKey) : details.reason;
  }
  return out;
}

export const translateApiError = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.code) {
      // STORAGE_OVER_CAP has two shapes: upload intent (bytesAvailable) and
      // plan change (bytesToDelete). Pick the copy that matches the payload.
      const key =
        err.code === "STORAGE_OVER_CAP" &&
        err.details &&
        typeof err.details.bytesToDelete === "number"
          ? "errors:STORAGE_OVER_CAP_DELETE"
          : errorTranslationKey(err.code);
      if (i18n.exists(key)) {
        return i18n.t(key, formatDetails(err.details, err.code));
      }
      if (process.env.NODE_ENV === "development") {
        console.warn(`Unknown API error code: ${err.code}`);
      }
    }
    if (err.fallbackMessage && err.fallbackMessage !== err.code) {
      return err.fallbackMessage;
    }
    return i18n.t("errors:GENERIC_ERROR");
  }
  if (err instanceof Error) return err.message;
  return i18n.t("errors:NETWORK_ERROR");
};
